import React from "react";
import { MONTHS, CHARITY_SLOTS, parseAmount, parseDate } from "./utils";

function buildGroupedData(rows) {
  const activeSlots = CHARITY_SLOTS.filter((i) =>
    rows.some((r) => r[`charity_${i}_name`]),
  );

  const charityNameSet = new Map();
  rows.forEach((row) => {
    activeSlots.forEach((i) => {
      const name = row[`charity_${i}_name`];
      if (name && !charityNameSet.has(name)) charityNameSet.set(name, true);
    });
  });
  const charityNames = [...charityNameSet.keys()].sort();

  const grouped = {};
  const allMonthKeys = new Map();

  rows.forEach((row) => {
    const vehicle = row["vehicle"];
    if (!vehicle) return;
    const date = parseDate(row["period_start_date"]);
    if (!date) return;

    const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
    if (!allMonthKeys.has(monthKey)) {
      allMonthKeys.set(
        monthKey,
        `${MONTHS[date.getMonth()]} ${date.getFullYear()}`,
      );
    }

    if (!grouped[vehicle]) grouped[vehicle] = {};
    if (!grouped[vehicle][monthKey]) {
      grouped[vehicle][monthKey] = { goalsMet: 0, charityData: {} };
    }

    const cell = grouped[vehicle][monthKey];

    const gm = parseFloat(row["goals_met"]);
    if (!isNaN(gm)) cell.goalsMet += gm;

    activeSlots.forEach((i) => {
      const name = row[`charity_${i}_name`];
      const amount = parseAmount(row[`charity_${i}_amount`]);
      const unit = row[`charity_${i}_unit`] ?? "";
      if (name && amount !== null) {
        if (!cell.charityData[name])
          cell.charityData[name] = { amount: 0, unit };
        cell.charityData[name].amount += amount;
      }
    });
  });

  const sortedMonths = [...allMonthKeys.entries()]
    .sort((a, b) => b[0].localeCompare(a[0]))
    .map(([key, label]) => ({ key, label }));

  const vehicles = Object.keys(grouped).sort();

  return { grouped, sortedMonths, vehicles, charityNames };
}

function fmtAmount(amount, unit) {
  if (amount == null) return "—";
  const formatted = amount.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return unit ? `${formatted} ${unit}` : formatted;
}

const thClass =
  "p-2 text-left whitespace-nowrap border border-primary-200 text-xs font-medium";
const tdClass = "p-2 whitespace-nowrap border border-neutral-200";

const VesselMonthSummaryTable = ({ rows }) => {
  const { grouped, sortedMonths, vehicles, charityNames } =
    buildGroupedData(rows);

  if (vehicles.length === 0) return null;

  const colsPerMonth = 1 + charityNames.length;

  return (
    <div className="mb-6">
      <h4 className="text-sm font-semibold text-neutral-600 mb-3">
        Vessel Summary by Month
      </h4>
      <div className="overflow-x-auto max-h-[50vh] overflow-y-auto">
        <table className="w-full text-sm border-collapse">
          <thead className="sticky top-0 z-10">
            <tr className="bg-primary-100">
              <th
                className="p-2 text-left whitespace-nowrap border border-primary-200"
                rowSpan={2}
              >
                Vehicle
              </th>
              {sortedMonths.map(({ key, label }) => (
                <th
                  key={key}
                  className="p-2 text-center whitespace-nowrap border border-primary-200"
                  colSpan={colsPerMonth}
                >
                  {label}
                </th>
              ))}
            </tr>
            <tr className="bg-primary-50">
              {sortedMonths.map(({ key }) => (
                <React.Fragment key={key}>
                  <th className={thClass}>Goals Met</th>
                  {charityNames.map((name) => (
                    <th key={name} className={thClass}>
                      {name}
                    </th>
                  ))}
                </React.Fragment>
              ))}
            </tr>
          </thead>
          <tbody>
            {vehicles.map((vehicle) => (
              <tr key={vehicle} className="border-t even:bg-neutral-50">
                <td className={`${tdClass} font-medium`}>{vehicle}</td>
                {sortedMonths.map(({ key }) => {
                  const cell = grouped[vehicle]?.[key];
                  return (
                    <React.Fragment key={key}>
                      <td className={tdClass}>{cell ? cell.goalsMet : "—"}</td>
                      {charityNames.map((name) => {
                        const data = cell?.charityData[name];
                        return (
                          <td key={name} className={tdClass}>
                            {data ? fmtAmount(data.amount, data.unit) : "—"}
                          </td>
                        );
                      })}
                    </React.Fragment>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default VesselMonthSummaryTable;
